
import { useRef } from "react"

import { cn } from "../../lib/utils"

/**
 * Path chooser (H-15): pill buttons in a radio group. Arrow keys move and select,
 * as in a native radio group (roving tabindex).
 * Phones show the short label; the full label stays the accessible name.
 */
export function JobChooser({
  label,
  options,
  value,
  onChange,
  variant = "pill",
  className,
}) {
  const refs = useRef([])
  const current = Math.max(0, options.findIndex((o) => o.id === value))

  const move = (from, dir) => {
    const next = (from + dir + options.length) % options.length
    refs.current[next]?.focus()
    onChange(options[next].id)
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        variant === "tile"
          ? "grid w-full grid-cols-2 gap-2 sm:gap-3"
          : "grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-center",
        className
      )}
    >
      {options.map((o, i) => {
        const checked = o.id === value

        return (
          <button
            key={o.id}
            ref={(el) => {
              refs.current[i] = el
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={o.label}
            tabIndex={i === current ? 0 : -1}
            onClick={() => onChange(o.id)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault()
                move(i, 1)
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault()
                move(i, -1)
              }
            }}
            className={cn(
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              variant === "tile"
                ? cn(
                    "flex min-h-14 flex-col items-start gap-0.5 rounded-xl border-[1.5px] bg-background px-3 py-2.5 text-left transition-colors sm:px-4 sm:py-3",
                    checked
                      ? "border-primary bg-mint"
                      : "border-border hover:border-ink-strong"
                  )
                : cn(
                    "inline-flex min-h-12 items-center justify-center gap-2 rounded-full border-[1.5px] px-4 text-sm font-semibold transition-colors sm:px-5 sm:text-[15px]",
                    checked
                      ? "border-charcoal bg-charcoal text-white"
                      : "border-border bg-background text-foreground hover:border-ink-strong"
                  )
            )}
          >
            {variant === "tile" ? (
              <>
                <span className="flex items-center gap-2 text-sm font-semibold sm:text-[15px]">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "size-3.5 shrink-0 rounded-full border-2",
                      checked
                        ? "border-primary bg-primary shadow-[inset_0_0_0_2px_var(--background)]"
                        : "border-input"
                    )}
                  />

                  <span className="sm:hidden">{o.short}</span>
                  <span className="max-sm:hidden">{o.label}</span>
                </span>

                {o.helper && (
                  <span className="pl-5.5 text-xs leading-snug text-muted-foreground max-sm:hidden">
                    {o.helper}
                  </span>
                )}
              </>
            ) : (
              <>
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-3 shrink-0 rounded-full border-2",
                    checked
                      ? "border-brand-bright bg-brand-bright shadow-[inset_0_0_0_2px_var(--charcoal)]"
                      : "border-input"
                  )}
                />

                <span className="sm:hidden">{o.short}</span>
                <span className="max-sm:hidden">{o.label}</span>
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}
